import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUpdateEffect } from 'react-use';
import noop from 'lodash/noop';

import ErrorReporter from '@lyearn/core/utils/ErrorReporter';
import { useTranslation } from '@lyearn/i18n';
import { BaseMenuList, ClickAwayListener, Popper } from '@lyearn/ui';
import useHandleKeyDown from '@/modules/corporate/modules/channel/views/ChannelDetails/components/MessageInput/plugins/Mentions/hooks/useHandleKeyDown';
import { MentionEntityTypeEnum } from '@/types/schema';

import useMentions from '../../hooks/useMentions';
import { classes, popperClassName } from '../../styles';
import MentionsListItem from '../MentionsListItem';
import HeadingListItem from './HeadingListItem';
import { HeadingItemType, ItemTypeEnum, MentionGroupListProps, RenderItemType } from './types';
import { getRenderItems } from './utils';

function GroupList(props: MentionGroupListProps) {
  const { t } = useTranslation();
  const {
    onItemClick: onClick,
    searchString,
    onClose,
    setView,
    popperProps,
    setInitialMentions,
    MentionsFactory,
    focusEditor,
  } = props;

  const ref = useRef<HTMLUListElement>(null);

  const [selectedItemIndex, setSelectedItemIndex] = useState(0);
  const [selectedHeader, setSelectedHeader] = useState<MentionEntityTypeEnum | null>(null);

  useEffect(() => {
    setSelectedItemIndex(0);
    setSelectedHeader(null);
  }, [searchString]);

  const { users, contents, events, totalCount } = useMentions({ searchText: searchString || '' });

  useUpdateEffect(() => {
    if (!totalCount) {
      onClose?.();
    }
  }, [onClose, totalCount]);

  useEffect(() => {
    setInitialMentions({ users, events, contents });
  }, [users, events, contents, setInitialMentions]);

  const onItemClick = useCallback(
    (item: Exclude<RenderItemType, HeadingItemType>) => {
      if (selectedHeader) {
        setView?.(selectedHeader);

        return;
      }
      //@ts-ignore
      const mentionNodeAttrs = MentionsFactory[item.type].getMentionNodeAttrs(item.item);
      onClick?.(mentionNodeAttrs);
    },
    [MentionsFactory, onClick, selectedHeader, setView],
  );

  const onRightArrowPress = useCallback(
    (item: RenderItemType) => {
      // header already selected then change view list
      if (selectedHeader) {
        setView?.(selectedHeader);

        return;
      }
      if (item.type !== ItemTypeEnum.Header) {
        setSelectedHeader(item.type);
      }
    },
    [selectedHeader, setView],
  );

  const onLeftArrowPress = useCallback(() => {
    if (selectedHeader) {
      setSelectedHeader(null);
    }
  }, [selectedHeader]);

  const renderItems = useMemo(
    () => getRenderItems({ users, contents, events, t }),
    [users, contents, events, t],
  );

  const keyboardNavItems = useMemo(
    () => renderItems.filter((item) => item.type !== ItemTypeEnum.Header),
    [renderItems],
  );

  useHandleKeyDown({
    ref,
    items: keyboardNavItems,
    onItemClick: onItemClick,
    selectedItemIndex,
    setSelectedItemIndex,
    onRightArrowPress,
    onLeftArrowPress,
    selectionChangeCallback: onLeftArrowPress,
    onClose,
    focusEditor,
  });

  return (
    <ClickAwayListener onClickAway={onClose || noop}>
      <Popper className={popperClassName} open={!!totalCount} {...popperProps}>
        <BaseMenuList ref={ref} className={classes.list}>
          {renderItems.map((item) => {
            if (item.type === ItemTypeEnum.Header) {
              return (
                <HeadingListItem
                  key={item.item.id}
                  heading={item.item.heading}
                  isSelected={selectedHeader === item.item.id}
                  onClick={() => setView?.(item.item.id)}
                />
              );
            }

            const BaseItem = MentionsFactory[item.type]?.BaseItem;
            if (!BaseItem) {
              ErrorReporter.error(
                new Error("MentionsFactory doesn't have BaseItem for type: " + item.type),
              );

              return null;
            }

            return (
              <MentionsListItem
                key={item.item.id}
                index={item.index}
                isSelected={!selectedHeader && selectedItemIndex === item.index}
                item={item.item}
                onClick={() => onItemClick(item)}>
                {
                  //@ts-ignore
                  <BaseItem item={item.item} />
                }
              </MentionsListItem>
            );
          })}
        </BaseMenuList>
      </Popper>
    </ClickAwayListener>
  );
}

export default GroupList;
